import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "../components/Reveal";
import { CheckCircle2, Beaker, Stethoscope, Award } from "lucide-react";

const apis = ["Antibiotics", "Anti-inflammatory", "Steroids", "Muscle-relaxants", "Receptor-specific drugs", "Proteins", "Vaccines", "Macromolecules", "Nano-drugs", "Cells"];

const pillars = [
  { icon: Award, t: "Unmatched pedigree", d: "100 years of cumulative experience in radiolabeling and scintigraphy clinical trials — a distinction held by no other team in India." },
  { icon: Beaker, t: "End-to-end science", d: "One partner from preformulation to Phase II, so your molecule never loses momentum between stages." },
  { icon: Stethoscope, t: "GCP-grade clinical rigour", d: "An expert doctors' team and coordinators with 8+ years' experience, delivering ICH GCP-compliant study reports." },
];

export default function About() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, -4]);

  return (
    <div className="bg-background">
      <section ref={heroRef} className="relative mx-auto max-w-[1400px] px-6 pb-16 pt-40 lg:px-10 lg:pt-48 grain">
        <div className="grid gap-12 lg:grid-cols-[1.3fr_0.7fr] lg:items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold">About Hypotho Research</p>
            <h1 className="mt-6 font-serif-display text-5xl font-semibold leading-[0.98] tracking-tight text-foreground md:text-6xl lg:text-[5.4rem]">
              {["Redefining", " how drugs"].map((line, i) => (
                <span key={line} className="block overflow-hidden pb-5 -mb-5">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.9, delay: 0.15 * i, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
              <span className="block overflow-hidden pb-5 -mb-5">
                <motion.span
                  className="block text-primary"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
                >
                  reach the body.
                </motion.span>
              </span>
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-8 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                Hypotho Research is a professional drug formulation company delivering expertise across
                every area of drug development and delivery. We analyse and solve serious problems for
                companies and organizations through solution packages built around your individual needs —
                from formulation development to in&nbsp;vitro / in&nbsp;vivo testing and proof-of-concept
                studies.
              </p>
            </Reveal>
          </div>

          {/* Hero image with 3D parallax — matches Home hero image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto w-full max-w-xs card-3d lg:mx-0"
          >
            <motion.div
              style={{ rotateZ: imgRotate }}
              className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-border bg-card shadow-luxe-lg"
            >
              <motion.img
                src="/assets/lungs.jpg"
                alt="Lungs"
                style={{ y: imgY, scale: imgScale }}
                className="h-full w-full object-cover object-center"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-8 lg:px-10">
        <div className="grid gap-6 md:grid-cols-3">
          {pillars.map((p, i) => (
            <Reveal key={p.t} delay={i * 0.1}>
              <div className="h-full rounded-2xl border border-border bg-card p-8 shadow-luxe">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/5 text-primary">
                  <p.icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold text-foreground">{p.t}</h3>
                <p className="mt-3 leading-relaxed text-muted-foreground">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-16 border-y border-border bg-muted/40">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 py-24 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold">Our mission</p>
            <h2 className="mt-4 font-serif-display text-3xl font-semibold text-foreground md:text-4xl">To redefine your brand.</h2>
            <p className="mt-5 leading-relaxed text-muted-foreground">
              Hypotho Research has deep expertise in gamma scintigraphy — a powerful, non-invasive and
              versatile technology used to scrutinise the in&nbsp;vivo behaviour of your formulations.
              Coupled with pharmacokinetic data, it reveals time and site of disintegration, gastric
              emptying, GI transit, intra/inter-individual differences, and drug deposition at a
              precise site. This technology is also approved by the USFDA for lung delivery of
              formulations.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-xs uppercase tracking-[0.25em] text-gold">What we do</p>
            <h2 className="mt-4 font-serif-display text-3xl font-semibold text-foreground md:text-4xl">Make our customers happy.</h2>
            <p className="mt-5 leading-relaxed text-muted-foreground">
              Our clinical department brings together an expert doctors&apos; team and clinical research
              coordinators with more than eight years of experience. Together they deliver a full
              clinical solution — protocol development, study documentation design and regulatory
              authority submission — through to close-out and the production of ICH GCP-compliant
              clinical study reports.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Our capabilities</p>
          <h2 className="mt-4 font-serif-display text-3xl font-semibold text-foreground md:text-4xl">APIs & molecules we can label</h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">Competence to radiolabel most chemical APIs and formulations across oral, injectable, eye-nasal, inhalational, dermal and medical-device categories — right down to the level of individual cells.</p>
        </Reveal>
        <div className="mt-10 flex flex-wrap gap-3">
          {apis.map((a, i) => (
            <Reveal key={a} delay={i * 0.04}>
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm text-muted-foreground shadow-luxe transition-colors duration-300 hover:border-gold hover:text-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary" strokeWidth={1.5} /> {a}
              </span>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}